import { useSearchParams } from 'react-router-dom'
import { ValueOf } from 'type-fest'

export const SEARCH_PARAMS = {
    EDIT: 'edit',
    CREATE: 'create',
    THREAD_ID: 'thread',
    TASK_ID: 'task',
} as const

type SearchParamValues = ValueOf<typeof SEARCH_PARAMS>

/**
 * Use this hook to get and set the query params for a form.
 * Motivation: We want to have a common way to handle the query params for forms.
 * The query params are used to determine if the form is in create mode or not.
 */
export function useFormQueryParams() {
    const [searchParams, setSearchParams] = useSearchParams()

    const addOrRemoveSearchParams = (searchParams: URLSearchParams, key: string, value?: string) => {
        if (value) {
            searchParams.set(key, value)
        } else {
            searchParams.delete(key)
        }
    }

    const updateSingleParam = (key: string, value?: string) => {
        const newSearchParams = new URLSearchParams(searchParams)
        addOrRemoveSearchParams(newSearchParams, key, value)
        setSearchParams(newSearchParams, { replace: true })
    }

    /**
     * Sometimes we need to update multuple params, to avoid race conditions use this function.
     */
    const updateMultipleParams = (updates: Partial<Record<SearchParamValues, string | undefined>>) => {
        const newSearchParams = new URLSearchParams(searchParams)
        Object.entries(updates).forEach(([key, value]) => {
            addOrRemoveSearchParams(newSearchParams, key, value)
        })

        setSearchParams(newSearchParams, { replace: true })
    }

    return {
        openTaskId: searchParams.get(SEARCH_PARAMS.TASK_ID),
        setOpenTaskId: (value?: string) => updateSingleParam(SEARCH_PARAMS.TASK_ID, value),
        activeThreadId: searchParams.get(SEARCH_PARAMS.THREAD_ID),
        setActiveThreadId: (value?: string) => updateSingleParam(SEARCH_PARAMS.THREAD_ID, value),
        createMode: searchParams.get(SEARCH_PARAMS.CREATE) === 'true',
        setCreateMode: (value?: boolean) => updateSingleParam(SEARCH_PARAMS.CREATE, value ? 'true' : undefined),
        updateMultipleParams,
        isEditMode: searchParams.get(SEARCH_PARAMS.EDIT) === 'true',
        setIsEditMode: (value?: boolean) => updateSingleParam(SEARCH_PARAMS.EDIT, value ? 'true' : undefined),
    }
}
